import React from 'react'
import { capitalize } from 'lodash'
import SingleRange from 'components/Filter/types/SingleRange'
import Switch from 'components/Input/Switch'

export const generationLimitCheck = (images, types, expansion) => {
  let maxScale = 1, maxImages = images, maxTypes = 1
  if (images <= 500) {
    maxScale = 4
    maxImages = 2000
    maxTypes = 10
  } else if (images > 500 && images <= 2000) {
    maxScale = 3
    maxImages = 6000
    maxTypes = 8
  } else if (images > 2000 && images <= 5000) {
    maxScale = 2
    maxImages = 10000
    maxTypes = 6
  } else if (images > 5000 && images <= 10000) {
    maxScale = 1.5
    maxImages = 15000
    maxTypes = 5
  } else {
    maxScale = 1
    maxImages = images
    maxTypes = 5
  }

  const MAX_DATASET_ITEMS = 10000
  const actualImages = Math.round(images * expansion)
  const isScaleLimitExceeded = expansion > maxScale
  const isGeneratedImagesLimitExceeded = actualImages > maxImages
  const isTypeLimitExceeded = types > maxTypes
  const isDatasetLimitExceeded = actualImages > MAX_DATASET_ITEMS

  const isValid = !isScaleLimitExceeded && !isGeneratedImagesLimitExceeded && !isTypeLimitExceeded && !isDatasetLimitExceeded

  return {
    isValid,
    isScaleLimitExceeded,
    isGeneratedImagesLimitExceeded,
    isTypeLimitExceeded,
    isDatasetLimitExceeded,
    maxScale,
    maxImages,
    maxTypes,
    maxDatasetItems: MAX_DATASET_ITEMS,
    actualImages,
    images,
    types,
    expansion
  }
}



export const addAugmentationLabels = (configs) => {
  return configs.map(config => {
    if (!config.augments || !config.augments.length) return { ...config }
    const augment = config.augments[0]
    let label = ''
    const typeMatch = augment.type.match(/^augment_(.*)$/)
    let typeLabel = typeMatch ? typeMatch[1].replace(/_/g, ' ') : augment.type
    typeLabel = typeLabel.replace(/\b\w/g, c => c.toUpperCase())
    let paramDetail = ''

    if ('blur_type' in (augment.parameters || {})) {
      const blurType = augment.parameters.blur_type
      if (blurType) {
        paramDetail = ` (${blurType.charAt(0).toUpperCase() + blurType.slice(1)})`
      }
    } else if ('flip_horizontal' in (augment.parameters || {}) || 'flip_vertical' in (augment.parameters || {})) {
      const { flip_horizontal, flip_vertical } = augment.parameters
      if (flip_horizontal && flip_vertical) {
        paramDetail = ' (Horizontal & Vertical)'
      } else if (flip_horizontal) {
        paramDetail = ' (Horizontal)'
      } else if (flip_vertical) {
        paramDetail = ' (Vertical)'
      }
    } else if ('noise_type' in (augment.parameters || {})) {
      const noiseType = augment.parameters.noise_type
      if (noiseType) {
        paramDetail = ` (${noiseType.charAt(0).toUpperCase() + noiseType.slice(1).replaceAll('_', ' ')})`
      }
    } else if ('crop_type' in (augment.parameters || {})) {
      const cropType = augment.parameters.crop_type
      if (cropType) {
        paramDetail = ` (${cropType.charAt(0).toUpperCase() + cropType.slice(1)})`
      }
    }
    label = typeLabel + paramDetail

    return { ...config, label }
  })
}

export const countAugmentationTypes = ({ augmentationTypes, blurTypes, noiseTypes, cropTypes, useDefaultSettings }) => {
  const blurCount = augmentationTypes.find(type => type.key === 'augment_blur') ? blurTypes.filter(type => type.confirmed).length : 0
  const noiseCount = augmentationTypes.find(type => type.key === 'augment_noise') ? noiseTypes.filter(type => type.confirmed).length : 0
  const cropCount = augmentationTypes.find(type => type.key === 'augment_crop') ? cropTypes.filter(type => type.confirmed).length : 0
  const otherCount = augmentationTypes.filter(type => !(type.key === 'augment_blur' || type.key === 'augment_noise' || type.key === 'augment_crop')).length
  const getTotalCouunt = useDefaultSettings ? augmentationTypes.length : otherCount + blurCount + noiseCount + cropCount

  return getTotalCouunt
}

export const mapAugmentationConfiguration = ({ selected, blurTypes, noiseTypes, cropTypes, useDefaultSettings }) => {
  return selected.map(item => {
    let content = []
    const itemValues = item.newValues || item.defaultValues
    switch (item.key) {
      case 'augment_blur':
        if (useDefaultSettings) {
          content = [`${capitalize(itemValues.blur_type)}, ${itemValues.kernel_size} of 10`]
        } else {
          blurTypes.forEach(type => {
            const blurValue = type.newValue
            blurValue && content.push(`${capitalize(type.blur_type)}, ${blurValue} of 10`)
          })
        }
        break
      case 'augment_flip':
        content = [
          itemValues.flip_vertical ? 'Vertical' :
            itemValues.flip_horizontal ? 'Horizontal' : ''
        ].filter(Boolean)
        break
      case 'augment_noise':
        if (useDefaultSettings) {
          content = [`${capitalize(itemValues.noise_type)}, ${itemValues.noise_level} of 100`]
        } else {
          noiseTypes.forEach(type => {
            const noiseValue = type.newValue
            noiseValue && content.push(`${capitalize(type.noise_type.replaceAll('_', ' '))}, ${noiseValue} of 100`)
          })
        }
        break
      case 'augment_denoise':
        content = [
          `${itemValues.kernel_size} of 10`
        ]
        break
      case 'augment_brightness':
        content = [
          `${itemValues.brightness_adjust > 0 ? '+' : ''}${itemValues.brightness_adjust}`
        ]
        break
      case 'augment_rotation':
        content = [
          `${itemValues.angle > 0 ? '+' : ''}${itemValues.angle}`
        ]
        break
      case 'augment_scale':
        content = [
          `${itemValues.scale_min} - ${itemValues.scale_max}`
        ]
        break
      case 'augment_crop':
        if (useDefaultSettings) {
          content = [`${capitalize(itemValues.crop_type)}, ${itemValues.crop_min_ratio} - ${itemValues.crop_max_ratio}`]
        } else {
          cropTypes.forEach(type => {
            const cropValue = type.new_min || type.new_max
            cropValue && content.push(`${capitalize(type.crop_type)}, ${type.new_min} - ${type.new_max}`)
          })
        }
        break
      case 'augment_contrast':
        content = [
          `${itemValues.contrast_adjust > 0 ? '+' : ''}${itemValues.contrast_adjust}`
        ]
        break
      case 'augment_shear':
        content = [
          `X: ${itemValues.shear_x > 0 ? '+' : ''}${itemValues.shear_x}`,
          `Y: ${itemValues.shear_y > 0 ? '+' : ''}${itemValues.shear_y}`
        ]
        break
      case 'augment_grayscale':
        content = [
          `Desaturation, ${itemValues.desaturation_factor}`
        ]
        break
      case 'augment_hue':
        content = [
          `${itemValues.hue_shift > 0 ? '+' : ''}${itemValues.hue_shift}`
        ]
        break
      case 'augment_exposure':
        content = [
          `Gamma +${itemValues.gamma}`
        ]
        break
      case 'augment_saturation':
        content = [
          `+${itemValues.saturation_factor}`
        ]
        break
      case 'augment_cutout':
        content = [
          `Cutouts ${itemValues.num_cutouts}`,
          `Min ${itemValues.size_min}`,
          `Max ${itemValues.size_max}`
        ]
        break
      default:
        content = []
    }
    return { ...item, content }
  })
}

export const mapAugmentationOptions = (params) => {
  const { selected, onValueChange, color, blurTypes, noiseTypes, cropTypes } = params
  const { options, defaultValues, newValues, key, helperTexts } = selected
  let mappedOptions = []
  const optionValues = newValues || defaultValues
  const renderRange = ({ name, title, value, newValue, maxValue, minValue, step, range, titleMax }) => {
    return <SingleRange
      name={name}
      title={title}
      titleMax={titleMax}
      onChange={(val) => onValueChange(selected, newValue(val))}
      value={value}
      maxValue={maxValue || 10}
      minValue={minValue || 0}
      step={step || 1}
      range={range}
      color={color}
      showLegend
      isSmall
      legendColor='label'
      borderColor='border'
      customClass='nomargin'
    />
  }

  if (key === 'augment_blur') {
    options.blur_type.forEach(type => {
      const blurType = blurTypes.find(blur => blur.blur_type === type)
      const blurValue = blurType.newValue || blurType.kernel_size
      const optionHelper = helperTexts?.[type]
      mappedOptions.push({
        name: capitalize(type),
        parentKey: key,
        key: type,
        value: blurType,
        expandedContent: <div className={color}>
          {optionHelper && <div className='augmentation-editor__helper viso-text-label'>{optionHelper}</div>}
          {renderRange({
            name: type,
            title: 'Intensity',
            value: blurValue,
            newValue: (val) => ({ blur_type: type, kernel_size: val })
          })}
        </div>
      })
    })
  } else if (key === 'augment_crop') {
    options.crop_type.forEach(type => {
      const cropType = cropTypes.find(crop => crop.crop_type === type)
      const cropMinValue = cropType.new_min || cropType.crop_min_ratio
      const cropMaxValue = cropType.new_max || cropType.crop_max_ratio
      const optionHelper = helperTexts?.[type]
      mappedOptions.push({
        name: capitalize(type),
        parentKey: key,
        key: type,
        value: cropType,
        expandedContent: <div className={color}>
          {optionHelper && <div className='augmentation-editor__helper viso-text-label'>{optionHelper}</div>}
          {renderRange({
            name: type,
            title: 'Min Ratio',
            titleMax: 'Max Ratio',
            value: [cropMinValue, cropMaxValue],
            newValue: (val) => ({ crop_type: type, crop_min_ratio: val[0], crop_max_ratio: val[1] }),
            maxValue: 1,
            step: 0.1,
            range: true
          })}
        </div>
      })
    })
  } else if (key === 'augment_scale') {
    let scaleHelper = null
    if (typeof optionValues?.scale_min === 'number' && typeof optionValues?.scale_max === 'number') {
      if (optionValues.scale_min >= 1 && optionValues.scale_max > 1) {
        scaleHelper = helperTexts?.zoom_in
      } else if (optionValues.scale_max <= 1 && optionValues.scale_min < 1) {
        scaleHelper = helperTexts?.zoom_out
      } else {
        scaleHelper = helperTexts?.random
      }
    } else {
      scaleHelper = helperTexts?.random
    }
    mappedOptions.push({
      name: 'Scale',
      key: 'augment_scale',
      value: optionValues,
      expandedContent: <div className={color}>
        {scaleHelper && <div className='augmentation-editor__helper viso-text-label'>{scaleHelper}</div>}
        {renderRange({
          name: 'augment_scale',
          title: 'Min Scale',
          titleMax: 'Max Scale',
          value: [optionValues.scale_min, optionValues.scale_max],
          newValue: (val) => ({ scale_min: val[0], scale_max: val[1] }),
          step: 0.1,
          range: true
        })}
      </div>
    })
  } else if (key === 'augment_flip') {
    let flipHelper = null
    if (optionValues.flip_horizontal && optionValues.flip_vertical) {
      flipHelper = helperTexts?.horizontal_vertical
    } else if (optionValues.flip_horizontal) {
      flipHelper = helperTexts?.horizontal
    } else if (optionValues.flip_vertical) {
      flipHelper = helperTexts?.vertical
    }
    mappedOptions.push({
      name: 'Flip',
      key: 'augment_flip',
      value: optionValues,
      expandedContent: <>
        {flipHelper && <div className='augmentation-editor__helper viso-text-label'>{flipHelper}</div>}
        <div>
          <Switch
            color={color}
            name="flipHorizontal"
            value={optionValues.flip_horizontal}
            text="Horizontal"
            onChange={() => onValueChange(selected, { flip_horizontal: !optionValues.flip_horizontal, flip_vertical: optionValues.flip_vertical })}
            toogleWithLabel
          />
        </div>
        <div className='mt_50'>
          <Switch
            color={color}
            name="flipVertical"
            value={optionValues.flip_vertical}
            text="Vertical"
            onChange={() => onValueChange(selected, { flip_vertical: !optionValues.flip_vertical, flip_horizontal: optionValues.flip_horizontal })}
            toogleWithLabel
          />
        </div>
      </>
    })
  } else if (key === 'augment_noise') {
    options.noise_type.forEach(type => {
      const noiseType = noiseTypes.find(noise => noise.noise_type === type)
      const noiseValue = noiseType.newValue || noiseType.noise_level
      const optionHelper = helperTexts?.[type]
      mappedOptions.push({
        name: capitalize(type.replaceAll('_', ' ')),
        key: type,
        parentKey: key,
        value: noiseType,
        expandedContent: <div className={color}>
          {optionHelper && <div className='augmentation-editor__helper viso-text-label'>{optionHelper}</div>}
          {renderRange({
            name: type,
            title: 'Intensity',
            value: noiseValue,
            newValue: (val) => ({ noise_level: val, noise_type: type }),
            maxValue: 100
          })}
        </div>
      })
    })
  } else if (key === 'augment_denoise') {
    const denoiseHelper = helperTexts?.median || helperTexts?.gaussian
    mappedOptions.push({
      name: 'Denoise',
      key: 'augment_denoise',
      value: optionValues,
      expandedContent: <div className={color}>
        {denoiseHelper && <div className='augmentation-editor__helper viso-text-label'>{denoiseHelper}</div>}
        {renderRange({
          name: 'denoise',
          title: 'Intensity',
          value: optionValues.kernel_size,
          newValue: (val) => ({ kernel_size: val }),
        })}
      </div>
    })
  } else if (key === 'augment_rotation') {
    const rotationHelper = (optionValues.angle % 90 === 0) ? helperTexts?.step : helperTexts?.random
    mappedOptions.push({
      name: 'Rotation',
      key: 'augment_rotation',
      value: optionValues,
      expandedContent: <div className={color}>        
        {rotationHelper && <div className='augmentation-editor__helper viso-text-label'>{rotationHelper}</div>}
        {renderRange({
          name: 'rotation',
          title: 'Angle',
          value: optionValues.angle,
          newValue: (val) => ({ angle: val }),
          maxValue: 180,
          minValue: -180
        })}
      </div>
    })
  } else if (key === 'augment_brightness') {
    const brightnessHelper = optionValues.brightness_adjust > 0 ? helperTexts?.brighten : (optionValues.brightness_adjust < 0 ? helperTexts?.darken : null)
    mappedOptions.push({
      name: 'Brightness',
      key: 'augment_brightness',
      value: optionValues,
      expandedContent: <div className={color}>        
        {brightnessHelper && <div className='augmentation-editor__helper viso-text-label'>{brightnessHelper}</div>}
        {renderRange({
          name: 'brightness',
          title: 'Adjustment',
          value: optionValues.brightness_adjust,
          newValue: (val) => ({ brightness_adjust: val }),
          maxValue: 100,
          minValue: -100
        })}
      </div>
    })
  } else if (key === 'augment_contrast') {
    const contrastHelper = optionValues.contrast_adjust > 0 ? helperTexts?.increase : (optionValues.contrast_adjust < 0 ? helperTexts?.decrease : null)
    mappedOptions.push({
      name: 'Contrast',
      key: 'augment_contrast',
      value: optionValues,
      expandedContent: <div className={color}>        
        {contrastHelper && <div className='augmentation-editor__helper viso-text-label'>{contrastHelper}</div>}
        {renderRange({
          name: 'contrast',
          title: 'Adjustment',
          value: optionValues.contrast_adjust,
          newValue: (val) => ({ contrast_adjust: val }),
          maxValue: 100,
          minValue: -100
        })}
      </div>
    })
  } else if (key === 'augment_shear') {
    const shearParts = []
    if (optionValues.shear_x && helperTexts?.x_axis) shearParts.push(helperTexts.x_axis)
    if (optionValues.shear_y && helperTexts?.y_axis) shearParts.push(helperTexts.y_axis)
    const shearHelper = shearParts.join(' ')
    mappedOptions.push({
      name: 'Shear',
      key: 'augment_shear',
      value: optionValues,
      expandedContent: <div className={color}>        
        {shearHelper && <div className='augmentation-editor__helper viso-text-label'>{shearHelper}</div>}
        <div>
          {renderRange({
            name: 'shear_x',
            title: 'X',
            value: optionValues.shear_x,
            newValue: (val) => ({ shear_x: val, shear_y: optionValues.shear_y }),
            maxValue: 1,
            minValue: -1,
            step: 0.1
          })}
        </div>
        <div className='mt_200'>
          {renderRange({
            name: 'shear_y',
            title: 'Y',
            value: optionValues.shear_y,
            newValue: (val) => ({ shear_x: optionValues.shear_x, shear_y: val }),
            maxValue: 1,
            minValue: -1,
            step: 0.1
          })}
        </div>
      </div>
    })
  } else if (key === 'augment_grayscale') {
    const grayscaleHelper = helperTexts?.[optionValues.grayscale_type]
    mappedOptions.push({
      name: 'Grayscale',
      key: 'augment_grayscale',
      value: optionValues,
      expandedContent: <div className={color}>        
        {grayscaleHelper && <div className='augmentation-editor__helper viso-text-label'>{grayscaleHelper}</div>}
        {renderRange({
          name: 'grayscale',
          title: 'Desaturation',
          value: optionValues.desaturation_factor,
          newValue: (val) => ({ desaturation_factor: val }),
          maxValue: 1,
          step: 0.1
        })}
      </div>
    })
  } else if (key === 'augment_hue') {
    const hueHelper = helperTexts?.shift
    mappedOptions.push({
      name: 'Shift',
      key: 'augment_hue',
      value: optionValues,
      expandedContent: <div className={color}>        
        {hueHelper && <div className='augmentation-editor__helper viso-text-label'>{hueHelper}</div>}
        {renderRange({
          name: 'hue',
          title: 'Shift',
          value: optionValues.hue_shift,
          newValue: (val) => ({ hue_shift: val }),
          maxValue: 180,
          minValue: -180
        })}
      </div>
    })
  } else if (key === 'augment_saturation') {
    const saturationHelper = optionValues.saturation_factor > 1 ? helperTexts?.increase : (optionValues.saturation_factor < 1 ? helperTexts?.decrease : null)
    mappedOptions.push({
      name: 'Saturation',
      key: 'augment_saturation',
      value: optionValues,
      expandedContent: <div className={color}>        
        {saturationHelper && <div className='augmentation-editor__helper viso-text-label'>{saturationHelper}</div>}
        {renderRange({
          name: 'saturation',
          title: 'Factor',
          value: optionValues.saturation_factor,
          newValue: (val) => ({ saturation_factor: val }),
          maxValue: 1000
        })}
      </div>
    })
  } else if (key === 'augment_exposure') {
    const exposureHelper = optionValues.gamma > 1 ? helperTexts?.increase : (optionValues.gamma < 1 ? helperTexts?.decrease : null)
    mappedOptions.push({
      name: 'Exposure',
      key: 'augment_exposure',
      value: optionValues,
      expandedContent: <div className={color}>        
        {exposureHelper && <div className='augmentation-editor__helper viso-text-label'>{exposureHelper}</div>}
        {renderRange({
          name: 'exposure',
          title: 'Gamma',
          value: optionValues.gamma,
          newValue: (val) => ({ gamma: val }),
          step: 0.1
        })}
      </div>
    })
  } else if (key === 'augment_cutout') {
    const cutoutHelper = helperTexts?.random
    mappedOptions.push({
      name: 'Cutout',
      key: 'augment_cutout',
      value: optionValues,
      expandedContent: <div className={color}>        
        {cutoutHelper && <div className='augmentation-editor__helper viso-text-label'>{cutoutHelper}</div>}
        <div>
          {renderRange({
            name: 'cutouts',
            title: 'Cutouts',
            value: optionValues.num_cutouts,
            newValue: (val) => ({ num_cutouts: val, size_min: optionValues.size_min, size_max: optionValues.size_max }),
            maxValue: 100,
            minValue: 1
          })}
        </div>
        <div className='mt_200'>
          {renderRange({
            name: 'cutout',
            title: 'Min Size',
            titleMax: 'Max Size',
            value: [optionValues.size_min, optionValues.size_max],
            newValue: (val) => ({ num_cutouts: optionValues.num_cutouts, size_min: val[0], size_max: val[1] }),
            maxValue: 1,
            step: 0.05,
            range: true
          })}
        </div>
      </div>
    })
  }

  return mappedOptions
}

export const generateConfigurations = (payload) => {
  const { selectedAugmentations, useDefaultSettings, blurTypes, noiseTypes, cropTypes } = payload

  let augmentationArray = []

  if (useDefaultSettings) {
    augmentationArray.push(...selectedAugmentations)
  } else {
    const removeMultiple = selectedAugmentations.filter(item => item.key !== 'augment_blur' && item.key !== 'augment_noise' && item.key !== 'augment_crop')

    // Group all confirmed blur types into a single config object
    const blurAugments = blurTypes.filter(type => type.confirmed).map(type => ({
      type: 'augment_blur',
      parameters: { kernel_size: type.newValue, blur_type: type.blur_type }
    }))
    if (blurAugments.length > 0) {
      augmentationArray.push({ grouped: true, augments: blurAugments, key: 'augment_blur' })
    }

    // Group all confirmed noise types into a single config object
    const noiseAugments = noiseTypes.filter(type => type.confirmed).map(type => ({
      type: 'augment_noise',
      parameters: { noise_level: type.newValue, noise_type: type.noise_type }
    }))
    if (noiseAugments.length > 0) {
      augmentationArray.push({ grouped: true, augments: noiseAugments, key: 'augment_noise' })
    }

    // Group all confirmed crop types into a single config object
    const cropAugments = cropTypes.filter(type => type.confirmed).map(type => ({
      type: 'augment_crop',
      parameters: { crop_type: type.crop_type, crop_min_ratio: type.new_min, crop_max_ratio: type.new_max }
    }))
    if (cropAugments.length > 0) {
      augmentationArray.push({ grouped: true, augments: cropAugments, key: 'augment_crop' })
    }

    // Add other augmentations as before
    augmentationArray.push(...removeMultiple)
  }

  // Build configurations array
  const configurations = augmentationArray.map(item => {
    if (item.grouped && Array.isArray(item.augments)) {
      // For grouped (blur/noise/crop), return all augments in one config
      return { augments: item.augments }
    } else {
      let getValues = {}
      if (useDefaultSettings) {
        getValues = item.defaultValues
      } else {
        const { confirmed, ...newValuesWithoutConfirmed } = item.newValues || {}
        getValues = newValuesWithoutConfirmed
      }
      return {
        augments: [
          {
            type: item.key,
            parameters: getValues
          }
        ]
      }
    }
  })
  return { configurations }
}

export const getSelectedType = (selected) => {
  const { defaultValues, newValues, key } = selected
  const optionValues = newValues || defaultValues

  if (key === 'augment_blur') {
    return optionValues.blur_type
  } else if (key === 'augment_noise') {
    return optionValues.noise_type
  } else if (key === 'augment_crop') {
    return optionValues.crop_type
  } else {
    return key
  }
}

export const getPreviewStyle = (selected) => {
  const { defaultValues, newValues, key } = selected
  const optionValues = newValues || defaultValues
  let previewStyle = {}

  if (key === 'augment_flip') {
    previewStyle = {
      transform: `scale(${optionValues.flip_horizontal ? -1 : 1}, ${optionValues.flip_vertical ? -1 : 1})`
    }
  } else if (key === 'augment_scale') {
    previewStyle = {
      transform: `scale(${optionValues.scale_max})`
    }
  } else if (key === 'augment_rotation') {
    previewStyle = {
      transform: `rotate(${optionValues.angle}deg)`
    }
  } else if (key === 'augment_brightness') {
    previewStyle = {
      filter: `brightness(${optionValues.brightness_adjust + 100}%)`
    }
  } else if (key === 'augment_contrast') {
    previewStyle = {
      filter: `contrast(${optionValues.contrast_adjust + 100}%)`
    }
  } else if (key === 'augment_shear') {
    previewStyle = {
      transform: `skew(${optionValues.shear_x * 90}deg, ${optionValues.shear_y * 90}deg)`
    }
  } else if (key === 'augment_grayscale') {
    previewStyle = {
      filter: `grayscale(${optionValues.desaturation_factor * 100}%)`
    }
  } else if (key === 'augment_hue') {
    previewStyle = {
      filter: `hue-rotate(${optionValues.hue_shift}deg)`
    }
  } else if (key === 'augment_saturation') {
    previewStyle = {
      filter: `saturate(${(optionValues.saturation_factor + 100)}%)`
    }
  } else if (key === 'augment_exposure') {
    previewStyle = optionValues.gamma
  } else {
    previewStyle = optionValues
  }
  return previewStyle
}

export const getRandomBetween = (x, y) => Math.random() * (y - x) + x

export const getRandomDirection = () => {
  const directions = ['left', 'right', 'top', 'bottom']
  const index = Math.floor(Math.random() * directions.length)
  return directions[index]
}

export const renderCutouts = (getValues) => {
  return Array.from({ length: getValues.num_cutouts }).map((_, index) => {
    const getSize = getRandomBetween(getValues.size_min, getValues.size_max) * 100

    return (
      <div
        key={index}
        className='augmentation-editor-preview__cutout'
        style={{
          width: `${getSize}%`,
          height: `${getSize}%`,
          left: `${getRandomBetween(0, 100)}%`,
          top: `${getRandomBetween(0, 100)}%`
        }}
      />
    )
  })
}

export function opencvGaussianBlur(srcCanvas, dstCanvas, ksize = 5) {
  const src = cv.imread(srcCanvas)
  const dst = new cv.Mat()
  const k = ksize % 2 === 1 ? ksize : ksize + 1
  cv.GaussianBlur(src, dst, new cv.Size(k, k), 0, 0, cv.BORDER_DEFAULT)
  cv.imshow(dstCanvas, dst)
  src.delete()
  dst.delete()
  return true
}

export function opencvMedianBlur(srcCanvas, dstCanvas, ksize = 3) {
  const src = cv.imread(srcCanvas)
  const dst = new cv.Mat()
  const k = ksize % 2 === 1 ? ksize : ksize + 1
  cv.medianBlur(src, dst, k)
  cv.imshow(dstCanvas, dst)
  src.delete()
  dst.delete()
  return true
}

export function opencvMotionBlur(srcCanvas, dstCanvas, ksize = 9) {
  const src = cv.imread(srcCanvas)
  const dst = new cv.Mat()
  let kernel = cv.Mat.zeros(ksize, ksize, cv.CV_32F)
  for (let i = 0; i < ksize; i++) kernel.floatPtr(Math.floor(ksize / 2), i)[0] = 1.0 / ksize
  cv.filter2D(src, dst, -1, kernel)
  cv.imshow(dstCanvas, dst)
  src.delete()
  dst.delete()
  kernel.delete()
  return true
}

export function opencvDefocusBlur(srcCanvas, dstCanvas, ksize = 15) {
  return opencvGaussianBlur(srcCanvas, dstCanvas, ksize)
}

export function jsGammaCorrection(srcCanvas, dstCanvas, gamma = 1.0) {
  const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true })
  const dstCtx = dstCanvas.getContext('2d', { willReadFrequently: true })
  const { width, height } = srcCanvas
  const srcData = srcCtx.getImageData(0, 0, width, height)
  const dstData = dstCtx.createImageData(width, height)

  const invGamma = 1 / gamma
  const lut = new Uint8ClampedArray(256)
  for (let i = 0; i < 256; i++) {
    lut[i] = Math.min(255, Math.max(0, Math.pow(i / 255, invGamma) * 255))
  }

  for (let i = 0; i < srcData.data.length; i += 4) {
    dstData.data[i] = lut[srcData.data[i]]
    dstData.data[i + 1] = lut[srcData.data[i + 1]]
    dstData.data[i + 2] = lut[srcData.data[i + 2]]
    dstData.data[i + 3] = srcData.data[i + 3]
  }

  dstCanvas.width = width
  dstCanvas.height = height
  dstCtx.putImageData(dstData, 0, 0)
}

export function opencvDenoise(srcCanvas, dstCanvas, ksize = 3) {
  const src = cv.imread(srcCanvas)
  const dst = new cv.Mat()
  const k = ksize % 2 === 1 ? ksize : ksize + 1
  cv.medianBlur(src, dst, k)
  cv.imshow(dstCanvas, dst)
  src.delete()
  dst.delete()
}

export function jsSaltPepperNoise(srcCanvas, dstCanvas, noiseLevel = 0.02) {
  const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true })
  const dstCtx = dstCanvas.getContext('2d', { willReadFrequently: true })
  const { width, height } = srcCanvas
  const srcData = srcCtx.getImageData(0, 0, width, height)
  const dstData = dstCtx.createImageData(width, height)
  for (let i = 0; i < srcData.data.length; i++) {
    dstData.data[i] = srcData.data[i]
  }
  const totalPixels = width * height
  const numSalt = Math.ceil(noiseLevel * totalPixels / 2)
  const numPepper = Math.ceil(noiseLevel * totalPixels / 2)
  for (let i = 0; i < numSalt; i++) {
    const x = Math.floor(Math.random() * width)
    const y = Math.floor(Math.random() * height)
    const idx = (y * width + x) * 4
    dstData.data[idx] = 255
    dstData.data[idx + 1] = 255
    dstData.data[idx + 2] = 255
  }
  for (let i = 0; i < numPepper; i++) {
    const x = Math.floor(Math.random() * width)
    const y = Math.floor(Math.random() * height)
    const idx = (y * width + x) * 4
    dstData.data[idx] = 0
    dstData.data[idx + 1] = 0
    dstData.data[idx + 2] = 0
  }
  dstCanvas.width = width
  dstCanvas.height = height
  dstCtx.putImageData(dstData, 0, 0)
}

export function jsSpeckleNoise(srcCanvas, dstCanvas, noiseLevel = 0.1) {
  const ctx = srcCanvas.getContext('2d', { willReadFrequently: true })
  const { width, height } = srcCanvas
  const srcData = ctx.getImageData(0, 0, width, height)
  const dstData = ctx.createImageData(width, height)
  for (let i = 0; i < srcData.data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const noise = 1 + randomNormal(0, noiseLevel)
      dstData.data[i + c] = clamp(srcData.data[i + c] * noise, 0, 255)
    }
    dstData.data[i + 3] = srcData.data[i + 3]
  }
  dstCanvas.width = width
  dstCanvas.height = height
  dstCanvas.getContext('2d', { willReadFrequently: true }).putImageData(dstData, 0, 0)

  function randomNormal(mean = 0, stddev = 1) {
    let u = 0, v = 0
    while (u === 0) u = Math.random()
    while (v === 0) v = Math.random()
    return mean + stddev * Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  }
  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val))
  }
}

export function jsGaussianNoise(srcCanvas, dstCanvas, noiseLevel = 10) {
  const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true })
  const dstCtx = dstCanvas.getContext('2d', { willReadFrequently: true })
  const { width, height } = srcCanvas
  const srcData = srcCtx.getImageData(0, 0, width, height)
  const dstData = dstCtx.createImageData(width, height)
  for (let i = 0; i < srcData.data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const noise = Math.round(randomNormal(0, noiseLevel))
      dstData.data[i + c] = clamp(srcData.data[i + c] + noise, 0, 255)
    }
    dstData.data[i + 3] = srcData.data[i + 3]
  }
  dstCanvas.width = width
  dstCanvas.height = height
  dstCtx.putImageData(dstData, 0, 0)

  function randomNormal(mean = 0, stddev = 1) {
    let u = 0, v = 0
    while (u === 0) u = Math.random()
    while (v === 0) v = Math.random()
    return mean + stddev * Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  }
  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val))
  }
}

export function applyNoiseEffectToCanvas(srcCanvas, dstCanvas, img, getNoiseValues, noiseFns) {
  srcCanvas.width = img.width
  srcCanvas.height = img.height
  dstCanvas.width = img.width
  dstCanvas.height = img.height
  const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true })
  srcCtx.drawImage(img, 0, 0)
  const noiseType = (getNoiseValues.noise_type || '').toLowerCase()
  const noiseLevel = getNoiseValues.noise_level || 10
  switch (noiseType) {
    case 'salt_pepper':
    case 'salt & pepper':
      noiseFns.jsSaltPepperNoise(srcCanvas, dstCanvas, noiseLevel / 100)
      break
    case 'speckle':
      noiseFns.jsSpeckleNoise(srcCanvas, dstCanvas, noiseLevel / 100)
      break
    default:
      noiseFns.jsGaussianNoise(srcCanvas, dstCanvas, noiseLevel)
  }
}

export function applyBlurEffectToCanvas(srcCanvas, dstCanvas, img, getBlurValues, opencvFns) {
  srcCanvas.width = img.width
  srcCanvas.height = img.height
  dstCanvas.width = img.width
  dstCanvas.height = img.height
  const ctx = srcCanvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(img, 0, 0)
  const blurType = (getBlurValues.blur_type || '').toLowerCase()
  const kernel = getBlurValues.kernel_size * 10
  switch (blurType) {
    case 'motion':
      opencvFns.opencvMotionBlur(srcCanvas, dstCanvas, kernel || 9)
      break
    case 'median':
      opencvFns.opencvMedianBlur(srcCanvas, dstCanvas, kernel || 3)
      break
    case 'defocus':
      opencvFns.opencvDefocusBlur(srcCanvas, dstCanvas, kernel || 15)
      break
    default:
      opencvFns.opencvGaussianBlur(srcCanvas, dstCanvas, kernel || 5)
  }
}

export const renderCrop = (getValues) => {
  const getSize = getRandomBetween(getValues.crop_min_ratio, getValues.crop_max_ratio) * 50

  if (getValues.crop_type === 'center') {
    return (
      <div className='augmentation-editor-preview__crop'>
        <div className='augmentation-editor-preview__crop-top' style={{ height: `${getSize}%` }} />
        <div className='augmentation-editor-preview__crop-bottom' style={{ height: `${getSize}%` }} />
        <div className='augmentation-editor-preview__crop-left' style={{ width: `${getSize}%` }} />
        <div className='augmentation-editor-preview__crop-right' style={{ width: `${getSize}%` }} />
      </div>
    )
  } else {
    const getDirection = getRandomDirection()
    const getProperty = getDirection === 'right' || getDirection === 'left' ? 'width' : 'height'

    return (
      <div className='augmentation-editor-preview__crop'>
        <div className={`augmentation-editor-preview__crop-${getDirection}`} style={{ [getProperty]: `${getSize * 2}%` }} />
      </div>
    )
  }
}